import { useRouterStore } from 'mobx-state-router';
import React, { useCallback, useContext } from 'react';

import { SideVisitContext } from 'src/components/pages/eventShow/SideVisitContext';
import { OBSERVABILITY_MY_DAY_TODAY } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { AcceptNextPatientCard } from 'src/myDayToday/components/AcceptNextPatientCard';
import { useProviderId } from 'src/myDayToday/domain/useProviderId';
import { useAcceptPopInClinicRequest } from 'src/myDayToday/hooks/useAcceptPopInClinicRequest';
import { useMyDayTodayVisits } from 'src/myDayToday/hooks/useMyDayTodayVisits';
import { useObservability } from 'src/observability/useObservability';
import logger from 'src/shared/util/logger';

export const VisitsSection = () => {
  const providerId = useProviderId();
  const { acceptNextPatientSection, unsignedVisits } = useMyDayTodayVisits(providerId);
  const { acceptPopInClinicRequest, isLoading } = useAcceptPopInClinicRequest();
  const enableObservability = useFeatureFlag(OBSERVABILITY_MY_DAY_TODAY);
  const sendStaffEvent = useObservability(enableObservability);
  const { setSideVisit } = useContext(SideVisitContext);
  const router = useRouterStore();

  const handleAccept = useCallback(async () => {
    try {
      const response = await acceptPopInClinicRequest();
      const eventId = response?.eventId;
      const patientId = response?.patientId;
      const picRequestId = response?.id;

      if (eventId && patientId && picRequestId) {
        sendStaffEvent({
          event_type: 'patient_accepted',
          patient_id: patientId,
          event_id: eventId,
          pic_request_id: picRequestId,
        });
      }

      if (eventId && patientId) {
        // Show the visit note in the right SideVisit pane, then navigate this same tab to the
        // patient page. We don't pin the tab here: patientShow's defaultTab lands snapshot-eligible
        // providers on the Clinical Snapshot (left/main column) and everyone else on Overview when
        // the CLINICAL_SNAPSHOT flag is off — avoiding a blank left pane. patientShow co-renders the
        // chosen tab and the SideVisit pane for non-visit tabs, giving a split-screen view. We do
        // not open the video call here — see ORCA-2449.
        setSideVisit(patientId, eventId);
        router.goTo('showPatient', { params: { id: patientId } });
      } else {
        logger.error('Accepted pop-in clinic request missing eventId or patientId', { response });
      }
    } catch (error) {
      logger.error('Error accepting pop-in clinic request', { error });
    }
  }, [acceptPopInClinicRequest, router, sendStaffEvent, setSideVisit]);

  const handleVisitClick = useCallback(
    (visitId: string) => {
      router.goTo('showEvent', { params: { id: visitId } });
    },
    [router],
  );

  return (
    <AcceptNextPatientCard
      section={acceptNextPatientSection}
      unsignedVisits={unsignedVisits}
      onAccept={handleAccept}
      onVisitClick={handleVisitClick}
      isAccepting={isLoading}
    />
  );
};
